import { Button, Group } from "@mantine/core";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useCallback, useState } from "react";
import { textMapIdeias } from "@/src/routes/gestao-inovacao-page/ExportIdeias";
import { Cards } from "@/src/routes/ideias-relatorio-visao-geral/IdeiasRelatorioVisaoGeralPage";
import { TIdeiaProcessada } from "../hooks/useIdeiasRelatorio";

const DEZ = 10;
const CEM_PORCENTO = 100;
const DOIS = 2;

interface ExportExcelProperties {
  cards: Cards;
  empresa: TEmpresa | undefined;
  dadosFiltrados: TIdeiaProcessada[];
  isLoading: boolean;
}

/* eslint-disable @typescript-eslint/no-floating-promises */
// prettier-ignore
const ExportExcel = ({ cards, empresa, dadosFiltrados, isLoading: isLoadingData }: ExportExcelProperties) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = useCallback(async () => {
    setIsLoading(true);
    try {
      const workbook = new ExcelJS.Workbook();

      // Aguarda o término de exportaIdeias
      exportaIdeias(workbook, dadosFiltrados?.map((ideia) => [
        ideia.regulamentoNome, // regulamento - não temos este campo diretamente
        ideia.autorNome,
        ideia.titulo,
        ideia.descricao,
        ideia.descricaoProblema,
        ideia.diferenciais,
        ideia.feedback,
        ideia.autorEmail,
        ideia.createdAt,
        ideia.curtidas,
        ideia.comentarios,
        0, // moedas - não implementado para ideias
        textMapIdeias[ideia.status]
      ]));

      // Aguarda o término de exportaCards
      exportaCards(workbook, cards);
      await saveExcel(workbook);

      // Pronto, só agora a planilha foi gerada e salva
    } catch (error) {
      console.error("Erro ao gerar planilha:", error);
    } finally {
      setIsLoading(false);
    }
  }, [empresa, cards, dadosFiltrados]);

  const handleClick = useCallback(() => {
    void handleExport();
  }, [handleExport]);

  return (
    <Group justify={"flex-end"}>
      <Button
        variant="filled"
        onClick={handleClick}
        loading={isLoading || isLoadingData}
        disabled={isLoadingData}
      >
        Exportar
      </Button>
    </Group>
  );

};
export default ExportExcel;

const formatarValor = (numero: number) => {
  return `${Math.round((numero + Number.EPSILON) * DEZ) / DEZ}%`;
};

const exportaCards = (workbook: ExcelJS.Workbook, cards: Cards): void => {
  if (!cards) return;
  const columns = ["Ideias", "Comentários", "Curtidas", "Usuários"];
  const worksheet = workbook.addWorksheet("Resumo");
  const headerRow = worksheet.addRow(columns);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });
  const unicos = new Set([
    ...cards.ideias.participantes,
    ...cards.comentarios.participantes,
    ...cards.curtidas.participantes
  ]).size;

  worksheet.addRow([cards.ideias.total, cards.comentarios.total, cards.curtidas.total, cards.colaboradoresAtivos]);
  worksheet.addRow([
    cards.ideias.participantes.size,
    cards.comentarios.participantes.size,
    cards.curtidas.participantes.size,
    cards.colaboradoresAtivos
  ]);

  worksheet.addRow([
    formatarValor(cards.ideias.porcentagem),
    formatarValor(cards.comentarios.porcentagem),
    formatarValor(cards.curtidas.porcentagem),
    formatarValor((unicos * CEM_PORCENTO) / cards.colaboradoresAtivos)
  ]);
  calculateAndSetColumnWidths(worksheet, columns);
};

const saveExcel = async (workbook: ExcelJS.Workbook) => {
  try {
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), `relatorio.xlsx`);
  } catch (error) {
    console.error("Erro ao escrever buffer:", error);
  }
};

// prettier-ignore
const exportaIdeias = (workbook: ExcelJS.Workbook, csvData: (string | number | Date | undefined)[][] | undefined) => {
  if (!csvData) return;
  const columns = ["Regulamento", "Autores", "Titulo", "Descrição", "Descrição do Problema", "Diferenciais", "Feedback", "Email Autor(es)", "Data Criação", "Curtidas", "Comentários", "Patrocínios", "Status"];
  const worksheet = workbook.addWorksheet('Ideias');
  const headerRow = worksheet.addRow(columns);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });
  if (Array.isArray(csvData)) {
    for (const rowData of csvData) {
      worksheet.addRow(rowData);
    }
  }
  calculateAndSetColumnWidths(worksheet, columns);
}

const calculateAndSetColumnWidths = (worksheet: ExcelJS.Worksheet, columns: string[]) => {
  const columnWidths = columns.map((col, index) => {
    let maxLength = col.length;
    worksheet.getColumn(index + 1).eachCell({ includeEmpty: true }, (cell) => {
      let cellValue = "";
      try {
        cellValue = cell.value !== null && cell.value !== undefined ? JSON.stringify(cell.value) : "";
      } catch (error) {
        console.error("Erro ao stringificar o valor da célula:", error);
      }
      maxLength = Math.max(maxLength, cellValue.length);
    });

    return maxLength < DEZ ? DEZ : maxLength + DOIS;
  });
  const validatedColumnWidths = columnWidths.map((width) => {
    if (typeof width !== "number" || width < 0) {
      throw new Error("Invalid column width");
    }
    return width;
  });
  for (const [index, column] of worksheet.columns.entries()) {
    // eslint-disable-next-line security/detect-object-injection
    column.width = validatedColumnWidths[index]; // já foi validado anteriormente, ignorar o warning do estlint
  }
};
